/**
 * vignette — a soft edge-darken that frames the scene, plus an optional biome
 * colour wash. Implemented as a world-space `vignette_mask` sprite (clear center
 * → opaque edge) re-centred on the camera every frame (so it stays screen-fixed)
 * at a high depth, tinted dark with 'normal' blend so the opaque edges DARKEN the
 * view (additive/screen can't darken). The optional wash is a big soft glow over
 * the view in 'screen' blend for a colour cast.
 *
 * VIEWPORT-AWARE RESILIENCE:
 * Because the worldContainer is fit-to-letterbox scaled, non-16:9 displays
 * (phones, ultrawide monitors, 16:10 laptops, and portrait/split windows) expose
 * significantly more world units along whichever axis is pillarboxed/letterboxed.
 * Sizing the quad to a fixed 1.06× camera frame left its opaque outer edges
 * visible inside the player's view, creating a razor-sharp floating rectangle.
 *
 * computeVignetteDimensions dynamically queries the screen/canvas aspect ratio
 * and applies an overscan pad (default 1.25), guaranteeing that the quad's
 * outer boundaries sit safely outside the visible viewport on any device.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { createSprite } from '../../../rendering/utils/Sprite';
import { hasSharedTexture } from '../../../rendering/PixiSprite';
import type { ISprite } from '../../../rendering/IRenderer';

export const VIGNETTE_OVERSCAN_PAD = 1.25;

export interface VignetteDimensions {
  width: number;
  height: number;
}

/**
 * Compute viewport-aware vignette dimensions in world coordinates.
 */
export function computeVignetteDimensions(
  cameraView: { width: number; height: number },
  screenSize?: { width: number; height: number } | null,
  pad = VIGNETTE_OVERSCAN_PAD,
): VignetteDimensions {
  let visibleW = cameraView.width;
  let visibleH = cameraView.height;

  if (
    screenSize &&
    screenSize.width > 0 &&
    screenSize.height > 0 &&
    cameraView.width > 0 &&
    cameraView.height > 0
  ) {
    const fitScale = Math.min(
      screenSize.width / cameraView.width,
      screenSize.height / cameraView.height,
    );
    if (fitScale > 0) {
      visibleW = screenSize.width / fitScale;
      visibleH = screenSize.height / fitScale;
    }
  }

  return {
    width: Math.max(cameraView.width, visibleW) * pad,
    height: Math.max(cameraView.height, visibleH) * pad,
  };
}

const buildVignette: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'vignette') return null;
  if (!hasSharedTexture('vignette_mask')) return null;

  const edge = createSprite(null, {
    x: 0, y: 0, initialTextureKey: 'vignette_mask', width: 100, height: 100, depth: 950, alpha: layer.edgeAlpha, parent: ctx.layerContainer,
  });
  if (!edge) return null;
  edge.setTint(layer.color);
  edge.setBlendMode('normal'); // darken the edges

  let wash: ISprite | null = null;
  if (layer.washColor !== undefined && layer.washAlpha && hasSharedTexture('glow_soft')) {
    wash = createSprite(null, {
      x: 0, y: 0, initialTextureKey: 'glow_soft', width: 100, height: 100, depth: 949, alpha: layer.washAlpha, parent: ctx.layerContainer,
    });
    if (wash) { wash.setTint(layer.washColor); wash.setBlendMode('screen'); }
  }

  // Keep the overlay covering the camera view each frame (screen-fixed).
  const fit = () => {
    const v = ctx.getCameraView();
    if (!v) return;
    const screenSize = ctx.getScreenSize?.() ?? (typeof window !== 'undefined' && window.innerWidth > 0 ? { width: window.innerWidth, height: window.innerHeight } : null);
    const dims = computeVignetteDimensions(v, screenSize, VIGNETTE_OVERSCAN_PAD);

    edge.setPosition(v.centerX, v.centerY);
    edge.setDisplaySize(dims.width, dims.height);
    if (wash) {
      wash.setPosition(v.centerX, v.centerY);
      wash.setDisplaySize(dims.width * 1.3, dims.height * 1.3);
    }
  };
  fit();
  const unsub = ctx.addTick(fit);

  return makeContentHandle({ sprites: wash ? [edge, wash] : [edge], onDestroy: () => unsub() });
};

registerEffect({ kind: 'vignette', build: buildVignette, depth: 950, mooded: false });
